/**
 * Canonical enum registry — the single source of truth for every status /
 * kind / mode string the API emits and the UI reads.
 *
 * Built from the mock-layer audit (PROJECT_ASSESSMENT.md §4F + the enum
 * catalogue, 2026-06-11), which found the same concept spelled several
 * different ways across mocks and inline page types. Each enum below is the
 * RESOLVED canonical set; the `Supersedes:` note records the variants it
 * replaces so the mechanical fixture sweep knows what to rewrite.
 *
 * Convention: lower_snake_case string values; one `as const` tuple per enum
 * (the single source), a Zod enum for validation, and an inferred TS type.
 * Mirrors NESTJS_SCHEMA.md enum sets so the future backend imports the same
 * registry (this dir is promoted to packages/contracts when apps/api lands).
 */
import { z } from 'zod';

/** Build a {VALUES, schema, Type} triple from one const tuple. */
function asEnum<const T extends readonly [string, ...string[]]>(values: T) {
  return { values, schema: z.enum(values) };
}

/* ── Subscription ─────────────────────────────────────────────────────── */
// Supersedes: the narrow owner-side Business.status `active|trial|past_due`
// (mocks/data.ts) — that was a display subset, not the real lifecycle.
export const SUBSCRIPTION_STATUS = ['trial', 'active', 'past_due', 'suspended', 'canceled'] as const;
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUS);
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number];

/* ── Business (lifecycle, distinct from its subscription) ─────────────── */
// Per SAAS_REARCHITECTURE_PLAN glossary: pending_payment → provisioning →
// active → suspended → canceled. trial/past_due are reflected from the
// subscription. Supersedes admin.ts `+pending_payment` ad-hoc set.
export const BUSINESS_STATUS = [
  'pending_payment', 'provisioning', 'active', 'trial', 'past_due', 'suspended', 'canceled',
] as const;
export const businessStatusSchema = z.enum(BUSINESS_STATUS);
export type BusinessStatus = (typeof BUSINESS_STATUS)[number];

/* ── Invoice ──────────────────────────────────────────────────────────── */
// Canonical = the BillingFlow lifecycle (the page itself labels it "NestJS
// schema §4.8"). Supersedes the narrow `paid|past_due|draft` in mocks/data.ts.
export const INVOICE_STATUS = [
  'draft', 'open', 'paid', 'partial_refunded', 'refunded', 'uncollectible', 'void',
] as const;
export const invoiceStatusSchema = z.enum(INVOICE_STATUS);
export type InvoiceStatus = (typeof INVOICE_STATUS)[number];

/* ── Payment ──────────────────────────────────────────────────────────── */
// Union of the drifting sets; `pending` is real for gateway-initiated,
// `disputed` for a chargeback (MONEY_OUT_SPEC §6).
export const PAYMENT_STATUS = ['pending', 'paid', 'failed', 'refunded', 'partial_refunded', 'disputed'] as const;
export const paymentStatusSchema = z.enum(PAYMENT_STATUS);
export type PaymentStatus = (typeof PAYMENT_STATUS)[number];

/* ── Payment method / gateway (CODES, never display strings) ──────────── */
// Supersedes the display strings 'SSLCommerz' | 'Cash' | 'Bank Transfer'.
export const PAYMENT_METHOD = [
  'sslcommerz', 'manual_cash', 'bank_transfer', 'bkash', 'nagad', 'stripe', 'paypal',
] as const;
export const paymentMethodSchema = z.enum(PAYMENT_METHOD);
export type PaymentMethod = (typeof PAYMENT_METHOD)[number];

export const GATEWAY_SLUG = ['sslcommerz', 'stripe', 'paypal', 'manual_cash'] as const;
export const gatewaySlugSchema = z.enum(GATEWAY_SLUG);
export type GatewaySlug = (typeof GATEWAY_SLUG)[number];

// Supersedes the literal em-dash `'—'` mode in mocks/admin.ts.
export const GATEWAY_MODE = ['live', 'sandbox', 'disabled'] as const;
export const gatewayModeSchema = z.enum(GATEWAY_MODE);
export type GatewayMode = (typeof GATEWAY_MODE)[number];

/* ── Reseller ─────────────────────────────────────────────────────────── */
// Decision #11: adds `pending_setup` (the mock omitted it).
export const RESELLER_STATUS = ['invited', 'pending_setup', 'active', 'frozen', 'terminated'] as const;
export const resellerStatusSchema = z.enum(RESELLER_STATUS);
export type ResellerStatus = (typeof RESELLER_STATUS)[number];

/* ── Reseller application ─────────────────────────────────────────────── */
// Canonical = the admin.ts DB-status set. The reseller.ts `application_*`
// ViewState values are a FRONTEND routing concern, mapped from these.
export const RESELLER_APPLICATION_STATUS = [
  'pending', 'in_review', 'more_info_needed', 'approved', 'rejected', 'withdrawn',
] as const;
export const resellerApplicationStatusSchema = z.enum(RESELLER_APPLICATION_STATUS);
export type ResellerApplicationStatus = (typeof RESELLER_APPLICATION_STATUS)[number];

export const APPLICATION_TIMELINE_KIND = ['submitted', 'note', 'status_change', 'reply'] as const;
export const applicationTimelineKindSchema = z.enum(APPLICATION_TIMELINE_KIND);
export type ApplicationTimelineKind = (typeof APPLICATION_TIMELINE_KIND)[number];

/* ── Package / Price ──────────────────────────────────────────────────── */
export const PACKAGE_STATUS = ['draft', 'published', 'deprecated', 'archived'] as const;
export const packageStatusSchema = z.enum(PACKAGE_STATUS);
export type PackageStatus = (typeof PACKAGE_STATUS)[number];

// A price is active/inactive within a published package — deliberately a
// different lifecycle from the package's `published`.
export const PRICE_STATUS = ['draft', 'active', 'deprecated', 'archived'] as const;
export const priceStatusSchema = z.enum(PRICE_STATUS);
export type PriceStatus = (typeof PRICE_STATUS)[number];

// Supersedes Subscription.interval `month|year` vs Price.interval drift.
export const BILLING_INTERVAL = ['month', 'quarter', 'year', 'one_time'] as const;
export const billingIntervalSchema = z.enum(BILLING_INTERVAL);
export type BillingInterval = (typeof BILLING_INTERVAL)[number];

/* ── Coupon ───────────────────────────────────────────────────────────── */
// Supersedes the `trial` (vs `trial_extension`) variant in the admin drawer.
export const COUPON_DISCOUNT_TYPE = ['percent', 'fixed', 'trial_extension', 'free_intervals'] as const;
export const couponDiscountTypeSchema = z.enum(COUPON_DISCOUNT_TYPE);
export type CouponDiscountType = (typeof COUPON_DISCOUNT_TYPE)[number];

export const COUPON_STATUS = ['draft', 'active', 'expired', 'disabled'] as const;
export const couponStatusSchema = z.enum(COUPON_STATUS);
export type CouponStatus = (typeof COUPON_STATUS)[number];

/* ── Add-on ───────────────────────────────────────────────────────────── */
export const ADDON_BILLING = ['recurring', 'one_time'] as const;
export const addonBillingSchema = z.enum(ADDON_BILLING);
export type AddonBilling = (typeof ADDON_BILLING)[number];

/* ── Payout (reseller) ────────────────────────────────────────────────── */
export const PAYOUT_METHOD = ['bank_transfer', 'bkash', 'nagad', 'manual'] as const;
export const payoutMethodSchema = z.enum(PAYOUT_METHOD);
export type PayoutMethod = (typeof PAYOUT_METHOD)[number];

export const PAYOUT_SCHEDULE = ['biweekly', 'monthly'] as const;
export const payoutScheduleSchema = z.enum(PAYOUT_SCHEDULE);
export type PayoutSchedule = (typeof PAYOUT_SCHEDULE)[number];

export const PAYOUT_DESTINATION_TYPE = ['bkash', 'bank'] as const;
export const payoutDestinationTypeSchema = z.enum(PAYOUT_DESTINATION_TYPE);
export type PayoutDestinationType = (typeof PAYOUT_DESTINATION_TYPE)[number];

/* ── Database / POS source ────────────────────────────────────────────── */
// Supersedes the three different "self-hosted" tokens `own` / `byo` /
// `selfHost` scattered across signup, new-business and customer-create.
export const DB_MODE = ['managed', 'self_hosted', 'legacy'] as const;
export const dbModeSchema = z.enum(DB_MODE);
export type DbMode = (typeof DB_MODE)[number];

/* ── Domain ───────────────────────────────────────────────────────────── */
export const DOMAIN_SCOPE = ['reseller', 'business'] as const;
export const domainScopeSchema = z.enum(DOMAIN_SCOPE);
export type DomainScope = (typeof DOMAIN_SCOPE)[number];

export const TLS_STATUS = ['issued', 'pending', 'failed'] as const;
export const tlsStatusSchema = z.enum(TLS_STATUS);
export type TlsStatus = (typeof TLS_STATUS)[number];

export const DNS_STATUS = ['verified', 'pending'] as const;
export const dnsStatusSchema = z.enum(DNS_STATUS);
export type DnsStatus = (typeof DNS_STATUS)[number];

/* ── Currency ─────────────────────────────────────────────────────────── */
export const CURRENCY_CODE = ['BDT', 'USD', 'GBP', 'EUR'] as const;
export const currencyCodeSchema = z.enum(CURRENCY_CODE);
export type CurrencyCode = (typeof CURRENCY_CODE)[number];

/* ── Roles ────────────────────────────────────────────────────────────── */
// Roles come from memberships, not a user-level flag (Decision #9). Three
// scopes, kept as separate enums so a value can't leak across scope.
export const PLATFORM_ROLE = ['platform_admin', 'platform_staff'] as const;
export const platformRoleSchema = z.enum(PLATFORM_ROLE);
export type PlatformRole = (typeof PLATFORM_ROLE)[number];

export const RESELLER_MEMBERSHIP_ROLE = ['admin', 'staff', 'readonly'] as const;
export const resellerMembershipRoleSchema = z.enum(RESELLER_MEMBERSHIP_ROLE);
export type ResellerMembershipRole = (typeof RESELLER_MEMBERSHIP_ROLE)[number];

export const BUSINESS_MEMBERSHIP_ROLE = ['admin', 'staff', 'readonly'] as const;
export const businessMembershipRoleSchema = z.enum(BUSINESS_MEMBERSHIP_ROLE);
export type BusinessMembershipRole = (typeof BUSINESS_MEMBERSHIP_ROLE)[number];

// The role label used by the audit log + JWT (cross-scope, coarse).
export const ACTOR_ROLE = [
  'platform_admin', 'platform_staff', 'reseller_admin', 'reseller_staff',
  'business_admin', 'business_staff', 'system',
] as const;
export const actorRoleSchema = z.enum(ACTOR_ROLE);
export type ActorRole = (typeof ACTOR_ROLE)[number];

/* ── OTP / MFA / sessions (Decisions #25, #26, #27) ───────────────────── */
export const OTP_KIND = ['signup', 'login', 'recovery'] as const;
export const otpKindSchema = z.enum(OTP_KIND);
export type OtpKind = (typeof OTP_KIND)[number];

export const OTP_DELIVERY = ['sms', 'email', 'both'] as const;
export const otpDeliverySchema = z.enum(OTP_DELIVERY);
export type OtpDelivery = (typeof OTP_DELIVERY)[number];

export const MFA_KIND = ['sms', 'email', 'totp_authy', 'totp_google'] as const;
export const mfaKindSchema = z.enum(MFA_KIND);
export type MfaKind = (typeof MFA_KIND)[number];

export const SESSION_REVOKED_REASON = [
  'user_signed_out', 'cap_exceeded', 'admin_revoked', 'password_changed', 'mfa_changed', 'expired',
] as const;
export const sessionRevokedReasonSchema = z.enum(SESSION_REVOKED_REASON);
export type SessionRevokedReason = (typeof SESSION_REVOKED_REASON)[number];

export const DEVICE_KIND = ['monitor', 'smartphone', 'tablet', 'laptop'] as const;
export const deviceKindSchema = z.enum(DEVICE_KIND);
export type DeviceKind = (typeof DEVICE_KIND)[number];

/* ── Broadcasts / notifications (Decision #28) ────────────────────────── */
export const BROADCAST_AUDIENCE = [
  'all_resellers', 'all_end_customers', 'reseller_customers', 'one_business',
] as const;
export const broadcastAudienceSchema = z.enum(BROADCAST_AUDIENCE);
export type BroadcastAudience = (typeof BROADCAST_AUDIENCE)[number];

export const DELIVERY_CHANNEL = ['notification_panel', 'login_modal', 'top_banner'] as const;
export const deliveryChannelSchema = z.enum(DELIVERY_CHANNEL);
export type DeliveryChannel = (typeof DELIVERY_CHANNEL)[number];

export const SEVERITY = ['info', 'warning', 'critical'] as const;
export const severitySchema = z.enum(SEVERITY);
export type Severity = (typeof SEVERITY)[number];

// Supersedes the 4-value audit notification category — clientBatch's 6-value
// superset is canonical (adds commission, application).
export const NOTIFICATION_CATEGORY = [
  'billing', 'security', 'system', 'team', 'commission', 'application',
] as const;
export const notificationCategorySchema = z.enum(NOTIFICATION_CATEGORY);
export type NotificationCategory = (typeof NOTIFICATION_CATEGORY)[number];

/* ── Audit log ────────────────────────────────────────────────────────── */
export const AUDIT_CATEGORY = [
  'authentication', 'user_management', 'business', 'subscription', 'payment',
  'configuration', 'security', 'system', 'platform', 'reseller', 'billing',
] as const;
export const auditCategorySchema = z.enum(AUDIT_CATEGORY);
export type AuditCategory = (typeof AUDIT_CATEGORY)[number];

export const AUDIT_OUTCOME = ['success', 'failure'] as const;
export const auditOutcomeSchema = z.enum(AUDIT_OUTCOME);
export type AuditOutcome = (typeof AUDIT_OUTCOME)[number];

export const AUDIT_SUBJECT_TYPE = [
  'user', 'business', 'reseller', 'subscription', 'invoice', 'payment',
  'package', 'coupon', 'domain', 'webhook', 'system', 'session', 'mfa',
] as const;
export const auditSubjectTypeSchema = z.enum(AUDIT_SUBJECT_TYPE);
export type AuditSubjectType = (typeof AUDIT_SUBJECT_TYPE)[number];

/* ── App / brand ──────────────────────────────────────────────────────── */
export const APP_CODE = ['ogrogoti', 'pos_signin', 'poc_signin'] as const;
export const appCodeSchema = z.enum(APP_CODE);
export type AppCode = (typeof APP_CODE)[number];

/* Re-export the helper so schemas.ts can build inline enums consistently. */
export { asEnum };
